/**
 * Reconciliação SISP
 * Importa os extratos de fecho do SISP (.xls do portal, que é HTML), reconcilia por
 * referência e classifica por tipo de serviço. Filtros por data/mês/ano, estado
 * (incl. erros) e serviço. Mostra o que a Pagali deve ao SISP e a comissão por promotor.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, Alert, CircularProgress, Stack, Divider, Grid,
  TextField, MenuItem, TablePagination,
} from '@mui/material';
import { CloudUpload, Sync, Receipt, FilterAltOff } from '@mui/icons-material';
import api from '../../../../services/api';

const cve = (n: number) => `${Math.round(Number(n) || 0).toLocaleString('pt-PT')}$`;
const MESES = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const SERVICOS = [
  { v: '', l: 'Todos os serviços' },
  { v: 'TICKET_PURCHASE', l: 'Bilhetes de evento' },
  { v: 'WALLET_TOPUP', l: 'Carregamento de carteira' },
  { v: 'SERVICE_PAYMENT', l: 'Pagamento de serviço' },
  { v: 'DONATION', l: 'Donativo' },
  { v: 'SHOP_ORDER', l: 'Loja (Shop)' },
  { v: 'OTHER', l: 'Outro' },
];

type Filtros = { estado: string; tipo_servico: string; ano: string; mes: string; de: string; ate: string; pos: string; q: string };
const EMPTY: Filtros = { estado: '', tipo_servico: '', ano: '', mes: '', de: '', ate: '', pos: '', q: '' };

export default function ReconciliacaoSISP() {
  const [files, setFiles] = useState<File[]>([]);
  const [importing, setImporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [filtros, setFiltros] = useState<Filtros>(EMPTY);
  const [resumo, setResumo] = useState<any>(null);
  const [promotores, setPromotores] = useState<any>(null);
  const [trans, setTrans] = useState<any>(null);
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(25);

  const qs = useCallback((extra: Record<string, any> = {}) => {
    const p = new URLSearchParams();
    Object.entries({ ...filtros, ...extra }).forEach(([k, v]) => { if (v !== '' && v != null) p.set(k, String(v)); });
    return p.toString();
  }, [filtros]);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [r, p, t] = await Promise.all([
        api.get(`/admin/sisp-reconciliation/resumo?${qs()}`),
        api.get(`/admin/sisp-reconciliation/por-promotor?${qs()}`),
        api.get(`/admin/sisp-reconciliation/transacoes?${qs({ limit: rpp, offset: page * rpp })}`),
      ]);
      setResumo(r.data?.data || null);
      setPromotores(p.data?.data || null);
      setTrans(t.data?.data || null);
    } catch (e: any) {
      setMsg({ type: 'error', text: 'Erro ao carregar: ' + (e?.response?.data?.error || e.message) });
    } finally { setLoading(false); }
  }, [qs, page, rpp]);
  useEffect(() => { carregar(); }, [carregar]);

  const importar = async () => {
    if (!files.length) { setMsg({ type: 'error', text: 'Escolhe pelo menos um ficheiro.' }); return; }
    setImporting(true); setMsg(null);
    try {
      const payload = { files: await Promise.all(files.map(async (f) => ({ filename: f.name, content: await f.text() }))) };
      const res = await api.post('/admin/sisp-reconciliation/import', payload);
      const d = res.data?.data || {};
      setMsg({ type: 'success', text: `Importado: ${d.linhas} linhas (${d.inseridos} novas, ${d.atualizados} atualizadas) · ${d.casados} casadas${d.outros_merchant ? ` · ${d.outros_merchant} ignoradas (outro merchant)` : ''}.` });
      setFiles([]); await carregar();
    } catch (e: any) { setMsg({ type: 'error', text: 'Erro ao importar: ' + (e?.response?.data?.error || e.message) }); }
    finally { setImporting(false); }
  };
  const reconciliar = async () => {
    setImporting(true);
    try { await api.post('/admin/sisp-reconciliation/reconciliar'); await carregar(); setMsg({ type: 'success', text: 'Reconciliação refeita.' }); }
    catch (e: any) { setMsg({ type: 'error', text: e?.response?.data?.error || e.message }); }
    finally { setImporting(false); }
  };

  const setF = (k: keyof Filtros, v: string) => { setPage(0); setFiltros((f) => ({ ...f, [k]: v })); };
  const anos = ['', '2026', '2025'];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>Reconciliação SISP</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Importa os extratos de fecho do SISP, vê o que a Pagali deve ao SISP, por serviço, por mês e por promotor.
      </Typography>
      {msg && <Alert severity={msg.type} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      {/* Upload */}
      <Card sx={{ mb: 2 }}><CardContent>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Button component="label" variant="outlined" startIcon={<CloudUpload />}>
            Escolher ficheiros (.xls)
            <input hidden type="file" multiple accept=".xls,.html,.htm" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </Button>
          <Typography variant="body2" color="text.secondary">{files.length ? files.map((f) => f.name).join(', ') : 'Nenhum ficheiro'}</Typography>
          <Button variant="contained" onClick={importar} disabled={importing || !files.length} startIcon={importing ? <CircularProgress size={16} /> : <CloudUpload />}>Importar</Button>
          <Button variant="text" onClick={reconciliar} disabled={importing} startIcon={<Sync />}>Refazer reconciliação</Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Usa o export original do portal SISP (Operações de Terminais). Vários POS de uma vez. Dedupe automático por transação.
        </Typography>
      </CardContent></Card>

      {/* Filtros */}
      <Card sx={{ mb: 2 }}><CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6} sm={3} md={2}><TextField select fullWidth size="small" label="Estado" value={filtros.estado} onChange={(e) => setF('estado', e.target.value)}>
            <MenuItem value="">Bem-sucedidas</MenuItem><MenuItem value="Error">Com erro</MenuItem><MenuItem value="all">Todas</MenuItem>
          </TextField></Grid>
          <Grid item xs={6} sm={3} md={2}><TextField select fullWidth size="small" label="Serviço" value={filtros.tipo_servico} onChange={(e) => setF('tipo_servico', e.target.value)}>
            {SERVICOS.map((s) => <MenuItem key={s.v} value={s.v}>{s.l}</MenuItem>)}
          </TextField></Grid>
          <Grid item xs={6} sm={3} md={1}><TextField select fullWidth size="small" label="Ano" value={filtros.ano} onChange={(e) => setF('ano', e.target.value)}>
            {anos.map((a) => <MenuItem key={a} value={a}>{a || 'Todos'}</MenuItem>)}
          </TextField></Grid>
          <Grid item xs={6} sm={3} md={1}><TextField select fullWidth size="small" label="Mês" value={filtros.mes} onChange={(e) => setF('mes', e.target.value)}>
            <MenuItem value="">Todos</MenuItem>{MESES.slice(1).map((m, i) => <MenuItem key={i + 1} value={String(i + 1)}>{m}</MenuItem>)}
          </TextField></Grid>
          <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" type="date" label="De" InputLabelProps={{ shrink: true }} value={filtros.de} onChange={(e) => setF('de', e.target.value)} /></Grid>
          <Grid item xs={6} sm={3} md={2}><TextField fullWidth size="small" type="date" label="Até" InputLabelProps={{ shrink: true }} value={filtros.ate} onChange={(e) => setF('ate', e.target.value)} /></Grid>
          <Grid item xs={6} sm={3} md={1}><TextField fullWidth size="small" label="Pesquisar ref" value={filtros.q} onChange={(e) => setF('q', e.target.value)} /></Grid>
          <Grid item xs={6} sm={3} md={1}><Button onClick={() => { setPage(0); setFiltros(EMPTY); }} title="Limpar filtros"><FilterAltOff /></Button></Grid>
        </Grid>
      </CardContent></Card>

      {loading ? <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box> : (<>
        {/* O que devo ao SISP */}
        <Card sx={{ mb: 2 }}><CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}><Receipt color="error" /><Typography variant="h6">O que a Pagali deve ao SISP</Typography></Stack>
          <Stack direction="row" spacing={4} sx={{ mb: 2 }} flexWrap="wrap">
            <Box><Typography variant="caption" color="text.secondary">Comissão SISP</Typography><Typography variant="h4" color="error" fontWeight={700}>{cve(resumo?.geral?.comissao_sisp)}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Volume</Typography><Typography variant="h5" fontWeight={600}>{cve(resumo?.geral?.volume)}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Transações</Typography><Typography variant="h5" fontWeight={600}>{resumo?.geral?.transacoes ?? 0}</Typography></Box>
          </Stack>
          {/* estados (clicável p/ ver erros) */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
            {(resumo?.porEstado || []).map((e: any, i: number) => (
              <Chip key={i} label={`${e.estado}: ${e.n}`} color={e.estado === 'Error' ? 'warning' : e.estado === 'Completed' ? 'success' : 'default'}
                variant={filtros.estado === e.estado ? 'filled' : 'outlined'} size="small" onClick={() => setF('estado', e.estado === 'Completed' ? '' : e.estado)} />
            ))}
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2">Por serviço</Typography>
              <Table size="small"><TableHead><TableRow><TableCell>Serviço</TableCell><TableCell align="right">N</TableCell><TableCell align="right">Volume</TableCell><TableCell align="right">Comissão</TableCell></TableRow></TableHead>
                <TableBody>{(resumo?.porServico || []).map((s: any, i: number) => (<TableRow key={i}><TableCell>{s.label}</TableCell><TableCell align="right">{s.n}</TableCell><TableCell align="right">{cve(s.volume)}</TableCell><TableCell align="right" sx={{ color: 'error.main' }}>{cve(s.comissao)}</TableCell></TableRow>))}
                  {!resumo?.porServico?.length && <TableRow><TableCell colSpan={4} align="center">Sem dados.</TableCell></TableRow>}</TableBody></Table>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2">Por mês</Typography>
              <Table size="small"><TableHead><TableRow><TableCell>Mês</TableCell><TableCell align="right">Comissão</TableCell></TableRow></TableHead>
                <TableBody>{(resumo?.porMes || []).map((m: any, i: number) => (<TableRow key={i}><TableCell>{m.mes}</TableCell><TableCell align="right" sx={{ color: 'error.main' }}>{cve(m.comissao)}</TableCell></TableRow>))}</TableBody></Table>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2">Por cartão</Typography>
              <Table size="small"><TableHead><TableRow><TableCell>Cartão</TableCell><TableCell align="right">Comissão</TableCell><TableCell align="right">Taxa</TableCell></TableRow></TableHead>
                <TableBody>{(resumo?.porCartao || []).map((c: any, i: number) => (<TableRow key={i}><TableCell><Chip size="small" label={c.tipo_cartao} /></TableCell><TableCell align="right">{cve(c.comissao)}</TableCell><TableCell align="right">{c.taxa_pct}%</TableCell></TableRow>))}</TableBody></Table>
            </Grid>
          </Grid>
        </CardContent></Card>

        {/* Por promotor */}
        <Card sx={{ mb: 2 }}><CardContent>
          <Typography variant="h6" gutterBottom>Comissão SISP por promotor / evento</Typography>
          <Table size="small"><TableHead><TableRow><TableCell>Promotor</TableCell><TableCell>Evento</TableCell><TableCell align="right">N</TableCell><TableCell align="right">Volume</TableCell><TableCell align="right">Comissão SISP</TableCell><TableCell align="right">Taxa</TableCell></TableRow></TableHead>
            <TableBody>{(promotores?.porEvento || []).map((e: any, i: number) => (<TableRow key={i}><TableCell>{e.promotor || e.entity_id || '—'}</TableCell><TableCell>{e.evento || `#${e.event_id}`}</TableCell><TableCell align="right">{e.transacoes}</TableCell><TableCell align="right">{cve(e.volume)}</TableCell><TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>{cve(e.comissao_sisp)}</TableCell><TableCell align="right">{e.taxa_pct}%</TableCell></TableRow>))}
              {!promotores?.porEvento?.length && <TableRow><TableCell colSpan={6} align="center">Sem transações casadas.</TableCell></TableRow>}</TableBody></Table>
          {Number(promotores?.orfas?.n) > 0 && <Alert severity="info" sx={{ mt: 2 }}>{promotores.orfas.n} transações não casadas a evento (serviços, carteira, tokenizados): comissão {cve(promotores.orfas.comissao)}.</Alert>}
        </CardContent></Card>

        {/* Transações */}
        <Card><CardContent>
          <Typography variant="h6" gutterBottom>Transações {filtros.estado === 'Error' ? '(com erro)' : ''}</Typography>
          <Table size="small"><TableHead><TableRow>
            <TableCell>Data</TableCell><TableCell>Cartão</TableCell><TableCell align="right">Valor</TableCell><TableCell align="right">Comissão</TableCell><TableCell>Estado</TableCell><TableCell>Serviço</TableCell><TableCell>Evento/Promotor</TableCell><TableCell>Ref</TableCell>
          </TableRow></TableHead>
            <TableBody>{(trans?.rows || []).map((r: any) => (<TableRow key={r.trx_id}>
              <TableCell>{r.data_trx ? new Date(r.data_trx).toLocaleString('pt-PT') : '—'}</TableCell>
              <TableCell>{r.tipo_cartao || '—'}</TableCell>
              <TableCell align="right">{cve(r.valor_cve)}</TableCell>
              <TableCell align="right" sx={{ color: 'error.main' }}>{cve(r.comissao_cve)}</TableCell>
              <TableCell><Chip size="small" label={r.estado} color={r.estado === 'Error' ? 'warning' : r.estado === 'Completed' ? 'success' : 'default'} /></TableCell>
              <TableCell>{r.servico_label}</TableCell>
              <TableCell>{r.evento ? `${r.evento}${r.promotor ? ' · ' + r.promotor : ''}` : '—'}</TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{r.merchant_ref || '—'}</TableCell>
            </TableRow>))}
              {!trans?.rows?.length && <TableRow><TableCell colSpan={8} align="center">Sem transações.</TableCell></TableRow>}</TableBody></Table>
          <TablePagination component="div" count={trans?.total || 0} page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rpp} onRowsPerPageChange={(e) => { setRpp(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[25, 50, 100, 200]} />
        </CardContent></Card>
      </>)}
    </Box>
  );
}
